import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import CensusErrorDataManyStep from './components/CensusErrorDataManyStep/CensusErrorDataManyStep';
import CensusErrorMismatchStep from './components/CensusErrorMismatchStep/CensusErrorMismatchStep';
import CensusErrorServiceStep from './components/CensusErrorServiceStep/CensusErrorServiceStep';
import CensusMismatchOverStep from './components/CensusMismatchOverStep/CensusMismatchOverStep';
import CensusProcessingStep from './components/CensusProcessingStep/CensusProcessingStep';
import CensusSuccessStep from './components/CensusSuccessStep/CensusSuccessStep';
import CensusValidatingStep from './components/CensusValidatingStep/CensusValidatingStep';
import CustomButton from '../../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import ModalCensus from '../../../../../../../shared/components/Modals/ModalCensus/ModalCensus';
import { MIN_EMPLOYEES_FOR_QUOTES } from '../../../../../../../shared/constants/quotes';
import { useCensusFlow } from '../../../../../../../shared/hooks/useCensusFlow/useCensusFlow';
import type {
  ICommercialAddon,
  ICensusValidationResult,
  IQuickCensusFileResponse
} from '../../../../../../../shared/interfaces/quotes';
import Quotes from '../../../../../../../shared/services/api/quotes/quotes';
import { TRANSLATIONS_QUOTES } from '../../../../../../../shared/translations/quotes';
import type { TobaccoStatus } from '../../../../../../../shared/types/quotes';
import { buildCensusPriceCalculationRequest, getAddonAgeRange } from '../../../../../../../shared/utils/quotes/quotes';

export interface CensusUploadModalProps {
  open: boolean;
  brokerId: string;
  employeesSize: string | number | null | undefined;
  productName?: string;
  addon?: ICommercialAddon | null;
  tobaccoStatus?: TobaccoStatus;
  onClose: () => void;
  onApply: (
    avgAge: number,
    summary: ICensusValidationResult['summary'],
    fileName: string,
    rowCount: number,
    fileObj?: IQuickCensusFileResponse
  ) => void;
  onUpdateHeadcount?: (next: number) => void;
}

export default function CensusUploadModal({
  open,
  brokerId,
  employeesSize,
  productName,
  addon,
  tobaccoStatus,
  onClose,
  onApply,
  onUpdateHeadcount
}: Readonly<CensusUploadModalProps>) {
  const { t } = useTranslation();
  const ageRange = getAddonAgeRange(addon);
  const flow = useCensusFlow({
    brokerId,
    employeesSize,
    onUpdateHeadcount,
    minAge: ageRange?.min,
    maxAge: ageRange?.max,
    addon
  });

  const { step, setStep, setSlowWarning, setPriceCalculationResult } = flow;
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (step !== 'processing') return;
    cancelledRef.current = false;

    const slowTimer = setTimeout(() => setSlowWarning(true), 8000);

    if (addon && flow.validationResult?.summary) {
      const request = buildCensusPriceCalculationRequest(
        addon,
        flow.validationResult.summary,
        productName,
        tobaccoStatus
      );

      Quotes.getPriceCalculation(brokerId, request)
        .then((result) => {
          if (!cancelledRef.current) {
            setPriceCalculationResult(result);
            setStep('success');
          }
        })
        .catch(() => {
          if (!cancelledRef.current) {
            setStep('error-service');
          }
        });
    } else {
      setStep('error-service');
    }

    return () => {
      cancelledRef.current = true;
      clearTimeout(slowTimer);
    };
  }, [
    step,
    addon,
    flow.validationResult,
    brokerId,
    productName,
    tobaccoStatus,
    setStep,
    setSlowWarning,
    setPriceCalculationResult
  ]);

  if (!open) return null;

  const TITLE_MAP: Record<string, string> = {
    validating: TRANSLATIONS_QUOTES.create.censusModal.validatingTitle,
    processing: TRANSLATIONS_QUOTES.create.censusModal.processingTitle,
    success: TRANSLATIONS_QUOTES.create.censusModal.successTitle,
    'error-data-many': TRANSLATIONS_QUOTES.create.censusModal.errorManyTitle,
    'error-mismatch': TRANSLATIONS_QUOTES.create.censusModal.errorMismatchTitle,
    'error-mismatch-over': TRANSLATIONS_QUOTES.create.censusModal.mismatchOverTitle,
    'error-service': TRANSLATIONS_QUOTES.create.censusModal.errorServiceTitle
  };

  const title = t(TITLE_MAP[flow.step] ?? TRANSLATIONS_QUOTES.create.censusModal.title);

  const expectedSize = Number.parseInt(String(employeesSize), 10) || 0;

  const cancelButton = (
    <CustomButton variant="default" size="m" onClick={onClose}>
      {t(TRANSLATIONS_QUOTES.create.censusModal.cancel)}
    </CustomButton>
  );

  const resetToUpload = () => {
    flow.setFile(null);
    flow.setStep('upload');
  };

  const renderBody = () => {
    switch (flow.step) {
      case 'validating':
        return <CensusValidatingStep />;
      case 'processing':
        return <CensusProcessingStep slowWarning={flow.slowWarning} />;
      case 'success':
        return (
          <CensusSuccessStep
            rowCount={flow.validationResult?.records.length ?? 0}
            avgAge={flow.avgAge}
            ageBandBreakdown={flow.ageBandBreakdown}
            addon={addon}
            tobaccoStatus={tobaccoStatus}
            productName={productName}
            summary={flow.validationResult?.summary}
          />
        );
      case 'error-data-many':
        return (
          <CensusErrorDataManyStep
            errors={flow.censusErrors.map((e) => ({
              ...e,
              message:
                e.code === 'invalid_format' ? t(TRANSLATIONS_QUOTES.create.censusModal.errorFormatDesc) : e.message
            }))}
          />
        );
      case 'error-mismatch':
        return (
          <CensusErrorMismatchStep mismatchCount={flow.mismatchCount} employeeHeadcount={MIN_EMPLOYEES_FOR_QUOTES} />
        );
      case 'error-mismatch-over':
        return (
          <CensusMismatchOverStep
            mismatchCount={flow.mismatchCount}
            employeeHeadcount={expectedSize}
            selectedOption={flow.mismatchOverOption}
            onOptionChange={flow.setMismatchOverOption}
          />
        );
      case 'error-service':
        return <CensusErrorServiceStep />;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (flow.step) {
      case 'validating':
      case 'processing':
        return cancelButton;
      case 'success':
        return (
          <>
            {cancelButton}
            <CustomButton
              variant="primary"
              size="m"
              disabled={!flow.validationResult || !flow.file || flow.avgAge === null}
              onClick={() => {
                onApply(
                  flow.avgAge!,
                  flow.validationResult!.summary,
                  flow.file!.name,
                  flow.validationResult!.records.length,
                  flow.validationResult!.file
                );
                onClose();
              }}
            >
              {t(TRANSLATIONS_QUOTES.create.censusModal.apply, { addon: productName })} →
            </CustomButton>
          </>
        );
      case 'error-data-many':
      case 'error-mismatch':
        return (
          <>
            {cancelButton}
            <CustomButton variant="primary" size="m" onClick={resetToUpload}>
              {t(
                flow.step === 'error-data-many'
                  ? TRANSLATIONS_QUOTES.create.censusModal.uploadCorrectedFile
                  : TRANSLATIONS_QUOTES.create.censusModal.uploadNewCensus
              )}
            </CustomButton>
          </>
        );
      case 'error-service':
        return (
          <>
            {cancelButton}
            <CustomButton variant="primary" size="m" onClick={flow.handleRetry}>
              {t(TRANSLATIONS_QUOTES.create.censusModal.tryAgain)} →
            </CustomButton>
          </>
        );
      case 'error-mismatch-over':
        return (
          <>
            {cancelButton}
            <CustomButton
              variant="primary"
              size="m"
              disabled={!flow.mismatchOverOption}
              onClick={flow.handleMismatchOverContinue}
            >
              {t(TRANSLATIONS_QUOTES.create.censusModal.footerContinue)}
            </CustomButton>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <ModalCensus
      open={open}
      onClose={onClose}
      title={title}
      step={flow.step}
      file={flow.file}
      onFileSelect={flow.handleFileSelect}
      onClear={() => flow.setFile(null)}
      onUpload={flow.handleUpload}
      uploadDisabled={!flow.file}
      onDownloadTemplate={flow.handleDownloadTemplate}
      onRetryUpload={resetToUpload}
      renderFooter={renderFooter}
    >
      {renderBody()}
    </ModalCensus>
  );
}
